import { createRouter, createWebHistory } from "vue-router";
import Home from "@/views/Home.vue";
import { oauthRoutes } from "@/router/oauthRoutes";
import { userRoutes } from "./userRoutes";
import { artistRoutes } from "./artistRoutes";
import { articleRoutes } from "./articleRoutes";
import Policy from "@/components/policy/Policy.vue";
import NotFound from "@/views/NotFound.vue";
import { supportRoutes } from "@/router/supportRoutes";
import { myPageRoutes } from "@/router/myPageRoutes";
import Search from "@/views/search/Search.vue";
import ServiceInfo from "@/views/Info/ServiceInfo.vue";
import Test from "@/views/Test.vue";
import { useAnalytics, USER_TYPES } from "@/composables/useAnalytics";
import { useScrollTracking } from "@/composables/useScrollTracking";
import { organizationRoutes } from "@/router/organizationRoutes";
import { USER_ROLE } from "@/common/common-enum";
import { globalScrollStore } from "@/stores/scrollStore";
import { SCROLL_STORAGE_KEYS, SCROLL_TIMING } from "@/utils/scrollConstants";
import { parseUserFromStorage } from "@/utils/userStorage";
import { processTokenFromUrl } from "@/utils/auth";
import { resetSeoMeta } from "@/composables/useSeoMeta";

const routes = [
  {
    path: "/",
    name: "Home",
    component: Home,
    meta: {
      analytics: {
        category: "home",
        type: "main",
      },
    },
  },
  {
    path: "/test12345",
    name: "Test",
    component: Test,
    meta: {
      analytics: {
        category: "test",
        type: "dev",
      },
    },
  },
  {
    path: "/search",
    name: "Search",
    component: Search,
    meta: {
      analytics: {
        category: 'search',
        type: 'main'
      },
      keepScrollPosition: true
    }
  },
  ...userRoutes,
  ...oauthRoutes,
  ...artistRoutes,
  ...articleRoutes,
  ...supportRoutes,
  ...myPageRoutes,
  ...organizationRoutes,
  {
    path: "/privacy",
    name: "PrivacyPolicy",
    component: Policy,
    meta: {
      analytics: {
        category: "policy",
        type: "privacy",
      },
    },
  },
  {
    path: "/privacy-optional",
    name: "PrivacyPolicyOptional",
    component: Policy,
    meta: {
      analytics: {
        category: "policy",
        type: "privacy_optional",
      },
    },
  },
  {
    path: "/terms",
    name: "TermsPolicy",
    component: Policy,
    meta: {
      analytics: {
        category: "policy",
        type: "terms",
      },
    },
  },
  {
    path: "/info/service",
    name: "ServiceInfo",
    component: ServiceInfo,
    meta: {
      analytics: {
        category: "info",
        type: "service",
      },
    },
  },
  {
    path: "/contest/applicants/:applicantId/portfolio",
    name: "ContestApplicantPortfolio",
    component: () => import("@/views/download/ApplicantPortfolio.vue"),
    meta: {
      analytics: {
        category: "contest",
        type: "applicant-portfolio",
      },
    },
  },
  {
    path: "/:pathMatch(.*)*",
    name: "NotFound",
    component: NotFound,
    meta: {
      analytics: {
        category: "error",
        type: "404",
      },
    },
  },
];

// Disable browser's native scroll restoration to prevent conflicts
if (typeof window !== 'undefined' && 'scrollRestoration' in history) {
  history.scrollRestoration = 'manual';
}


// 메모리 기반 페이지네이션 플래그 (sessionStorage 제한 환경 대응)
let memoryPaginationFlag = false;
window.setMemoryPaginationFlag = (value) => {
  memoryPaginationFlag = value;
};

/**
 * 스크롤 위치를 지연과 함께 부드럽게 적용하는 헬퍼 함수
 * @param {Object} position - 스크롤 위치 객체
 * @returns {Promise<Object>} Promise로 감싼 스크롤 위치 객체
 */
function createSmoothScrollPromise(position) {
  return new Promise(resolve => {
    setTimeout(() => {
      resolve({
        ...position,
        behavior: 'smooth'
      });
    }, SCROLL_TIMING.ROUTER_DELAY);
  });
}

const router = createRouter({
  history: createWebHistory(process.env.BASE_URL),
  routes,
  async scrollBehavior(to, from, savedPosition) {
    // 페이지네이션 네비게이션 체크 (sessionStorage + 메모리 플래그)
    let isPaginationNavigation = false;
    try {
      isPaginationNavigation = sessionStorage.getItem(SCROLL_STORAGE_KEYS.PAGINATION_NAVIGATION) === 'true';
    } catch (error) {
      console.warn('SessionStorage unavailable, using memory flag');
      isPaginationNavigation = memoryPaginationFlag;
    }

    if (isPaginationNavigation) {
      return false; // 컴포넌트가 처리
    }

    // keepScrollPosition 메타가 있는 라우트의 스토어 위치 복원
    if (to.meta?.keepScrollPosition) {
      const savedScroll = globalScrollStore.getPosition(to.fullPath);
      if (savedScroll) {
        return createSmoothScrollPromise({
          left: 0,
          top: savedScroll.scrollY
        });
      }
    }

    // 브라우저 저장 위치 복원 (뒤로가기/앞으로가기)
    if (savedPosition) {
      return createSmoothScrollPromise(savedPosition);
    }

    // 기본값: 상단으로
    return { left: 0, top: 0 };
  }
});

router.beforeEach(async (to, from, next) => {
  // 사용자 정보 파싱 (공통 함수 사용)
  const existingUser = parseUserFromStorage();
  
  const token = to.query.token;
  if (token && typeof token === 'string') {
    // 보안: 이미 로그인된 사용자의 세션을 보호하기 위해 OAuth 토큰 처리 제한
    if (existingUser) {
      console.warn('OAuth 토큰이 감지되었지만 사용자가 이미 로그인되어 있어 무시됩니다.');
      next({ path: to.path, query: { ...to.query, token: undefined }, replace: true });
      return;
    }
    
    const result = await processTokenFromUrl(token);
    const redirectTarget = result.success
      ? { path: '/', replace: true }
      : { 
          path: '/', 
          query: { 
            error: result.errorType || 'processing_error',
            message: encodeURIComponent(result.message || '로그인 처리 중 오류가 발생했습니다.')
          }, 
          replace: true 
        };
    
    next(redirectTarget);
    return;
  }

  // 페이지네이션 네비게이션 시에는 이미 저장된 위치를 덮어쓰지 않음 (sessionStorage + 메모리 플래그)
  let isPaginationNavigation = false;
  try {
    isPaginationNavigation = sessionStorage.getItem(SCROLL_STORAGE_KEYS.PAGINATION_NAVIGATION) === 'true';
  } catch (error) {
    console.warn('SessionStorage unavailable, using memory flag');
    isPaginationNavigation = memoryPaginationFlag;
  }

  // keepScrollPosition 메타가 있는 라우트 이탈 시 스크롤 위치 저장
  if (from.meta?.keepScrollPosition && from.fullPath !== to.fullPath && !isPaginationNavigation) {
    globalScrollStore.savePosition(from.fullPath, window.scrollY);
  }

  // 사용자 정보는 이미 위에서 파싱함 (existingUser)
  const user = existingUser;

  // 로그인 여부 체크 (parseUserFromStorage와 일관성 유지)
  const isAuthenticated = !!user;

  if (to.path.startsWith("/my/") && !isAuthenticated) {
    alert("로그인 후 이용하실 수 있습니다.");
    next("/");
    return;
  }

  // 조직 관련 페이지는 BUSINESS 역할만 접근 가능
  if (to.path.startsWith("/organization/")) {
    if (!user || !user.roleType || user.roleType !== USER_ROLE.BUSINESS) {
      alert("조직 관리자만 접근할 수 있습니다.");
      next("/");
      return;
    }
  }

  // 아티스트 프로필 페이지는 ARTIST 역할만 접근 가능 (정확한 경로 매칭)
  if (to.path === "/my/artist/profile" || to.path.startsWith("/my/artist/profile/")) {
    if (!user || !user.roleType || !user.roleType.startsWith(USER_ROLE.ARTIST)) {
      alert("아티스트만 접근할 수 있습니다.");
      next("/");
      return;
    }
  }

  next();
});
let currentScrollTracker = null;
let scrollTrackingTimeout = null;

// Google Analytics 페이지 추적 및 스크롤 추적
router.afterEach((to, from) => {
  // 이전 페이지의 meta 를 먼저 지운다. 상세 페이지는 데이터 도착 후 덮어쓴다 (mimio#59).
  resetSeoMeta(to);

  const { trackPageView, setUserProperties } = useAnalytics();

  if (scrollTrackingTimeout) {
    clearTimeout(scrollTrackingTimeout);
    scrollTrackingTimeout = null;
  }

  if (currentScrollTracker) {
    currentScrollTracker.stopTracking();
    currentScrollTracker = null;
  }

  const user = parseUserFromStorage();
  const userType =
    user && (user.id || user.accessToken)
      ? (user.userRoleType || user.roleType) === "ARTIST"
        ? USER_TYPES.ARTIST
        : USER_TYPES.MEMBER
      : USER_TYPES.GUEST;

  const pageTitle =
    to.meta?.title || (to.name ? `${to.name} | ARTICLE21` : "ARTICLE21");

  const getPageCategory = (route) => {
    return route.meta?.analytics?.category || "other";
  };

  const pageCategory = getPageCategory(to);
  const pageSubType = to.meta?.analytics?.type || "unknown";

  setUserProperties(userType, user?.id, {
    page_category: pageCategory,
    user_id: user?.id || null,
    artist_verified: (user?.userRoleType || user?.roleType) === "ARTIST" || false,
  });

  trackPageView(to.path, pageTitle, {
    content_category: pageCategory,
    content_type: pageSubType,
    user_type: userType,
    previous_page: from.path || "direct",
    route_name: to.name || "unknown",
    has_analytics_meta: !!to.meta?.analytics,
  });

  scrollTrackingTimeout = setTimeout(() => {
    if (scrollTrackingTimeout) {
      currentScrollTracker = useScrollTracking();
      currentScrollTracker.startTracking();
      scrollTrackingTimeout = null;
    }
  }, 500);
});


export default router;
